import React, { useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Loader2, UploadCloud, X } from "lucide-react";

export const card = "bg-[#241a17] border border-[#D4AF37]/15 rounded-xl";
export const inputCls = "w-full bg-[#1C1210] border border-[#D4AF37]/20 rounded-lg px-3.5 py-2.5 text-sm text-ivory placeholder:text-ivory/30 focus:outline-none focus:border-gold/60";
export const labelCls = "text-[0.7rem] uppercase tracking-widest text-ivory/50 mb-1.5 block";

export function Field({ label, children }) {
  return <div className="mb-4"><label className={labelCls}>{label}</label>{children}</div>;
}

export function Input(props) { return <input {...props} className={inputCls} />; }
export function Textarea(props) { return <textarea {...props} className={inputCls} rows={props.rows || 3} />; }
export function Select({ options, ...props }) {
  return <select {...props} className={inputCls}>{options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}</select>;
}

export function Btn({ children, variant = "gold", className = "", ...props }) {
  const styles = {
    gold: "bg-gold text-velvet hover:bg-gold-bright",
    outline: "border border-gold/40 text-gold hover:bg-gold/10",
    ghost: "text-ivory/70 hover:text-ivory hover:bg-white/5",
    danger: "border border-red-500/40 text-red-400 hover:bg-red-500/10",
  };
  return <button {...props} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${styles[variant]} ${className}`}>{children}</button>;
}

export function Badge({ children, tone = "gold" }) {
  const tones = {
    gold: "bg-gold/15 text-gold border-gold/30",
    green: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    blue: "bg-sky-500/15 text-sky-400 border-sky-500/30",
    red: "bg-red-500/15 text-red-400 border-red-500/30",
    gray: "bg-white/5 text-ivory/60 border-white/10",
  };
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-[0.68rem] tracking-wide border ${tones[tone] || tones.gold}`}>{children}</span>;
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className={`relative ${card} p-6 w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto dark-scroll`}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl text-gold">{title}</h3>
          <button onClick={onClose} className="text-ivory/50 hover:text-ivory"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ImageUpload({ value, onChange, label = "Image" }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch { alert("Upload failed"); } finally { setBusy(false); }
  };
  return (
    <Field label={label}>
      <div className="flex items-center gap-3">
        {value ? <img src={fileUrl(value)} alt="" className="w-16 h-16 rounded-lg object-cover border border-gold/20" /> : <div className="w-16 h-16 rounded-lg bg-[#1C1210] border border-gold/20 flex items-center justify-center text-ivory/30"><UploadCloud className="w-5 h-5" /></div>}
        <div className="flex-1">
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload" className={inputCls} />
          <label className="mt-2 inline-flex items-center gap-2 text-xs text-gold cursor-pointer hover:text-gold-bright">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />} Upload file
            <input type="file" accept="image/*,video/*" onChange={upload} className="hidden" />
          </label>
        </div>
      </div>
    </Field>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "gold" }) {
  return (
    <div className={`${card} p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-ivory/50 text-xs uppercase tracking-widest">{label}</p>
          <p className="font-serif text-3xl text-ivory mt-2">{value}</p>
        </div>
        {Icon && <span className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center"><Icon className="w-5 h-5 text-gold" /></span>}
      </div>
    </div>
  );
}
